import { type CountryCode } from "libphonenumber-js";
import { parsePhoneNumber } from "react-phone-number-input";

import { type Countries } from "types/countries";

/**
 * Best-effort normalize a phone number string to E.164 ("+15551234567") so
 * iOS and Android dial it without guessing. Parses through libphonenumber
 * (via react-phone-number-input) using `defaultCountry` as the assumed
 * region when the number is missing a country prefix.
 *
 * Falls back to stripping the input down to dial-pad characters when the
 * parser can't make sense of it — unparseable numbers still try to dial
 * what the user typed, just without E.164's country-code guarantee.
 */
function normalizePhone(value: string, defaultCountry?: Countries): string {
    const trimmed = value.trim();
    if (!trimmed) {
        return trimmed;
    }
    try {
        const parsed = parsePhoneNumber(trimmed, defaultCountry as CountryCode | undefined);
        if (parsed?.number) {
            return parsed.number;
        }
    } catch {
        // parser throws on some malformed inputs; fall through to the strip
    }
    return trimmed.replace(/[^\d+]/g, "");
}

/**
 * Builds a `tel:` URI from a phone number string. The number is parsed to
 * E.164 when possible so the device dialer doesn't have to guess the
 * country code (a particular iOS quirk on un-prefixed numbers).
 *
 * `defaultCountry` should be the contact's or the builder's country; pass
 * `undefined` when no locale is known and the helper will return the raw
 * stripped digits.
 */
export function buildTelHref(value: string, defaultCountry?: Countries): string {
    return `tel:${normalizePhone(value, defaultCountry)}`;
}

/**
 * Builds an `sms:` URI from a phone number string. Same parsing rules as
 * `buildTelHref`.
 */
export function buildSmsHref(value: string, defaultCountry?: Countries): string {
    return `sms:${normalizePhone(value, defaultCountry)}`;
}
